/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Box, getColor, IconUserOutlined, Menu, useTheme } from "@powerledger/ui-component-lib";
import { FC, Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ReactTooltip from "react-tooltip";

import { LogOutSVG } from "@/assets/icons/vectors/index";

import { useLogout } from "../../hooks";
import { LayoutHeader } from "./header";
import { LayoutProps, LogoutLinkProps } from "./layout.types";
import { LayoutView } from "./layout-view";
import { LayoutSider } from "./sider";
import { getIcons, useLayout } from "./use-layout";

const LogoutLink: FC<LogoutLinkProps> = ({ sidebarOpen }) => {
  const { t } = useTranslation();
  const { logout } = useLogout();
  return (
    <Menu.Item handleOnClick={logout} icon={<LogOutSVG />} key={"Logout"} active={false} iconOnly={!sidebarOpen}>
      {t("Log out")}
    </Menu.Item>
  );
};

export const Layout: FC<LayoutProps> = ({ children, toggles, sider, header, footer, userRoles }) => {
  const { t } = useTranslation();
  const { theme }: { theme: any } = useTheme();
  const navigate = useNavigate();
  const {
    currentRoute,
    sidebarOpen,
    filteredRoutes,
    unreadMessageCount,
    updateSidebarState,
    userInitials,
    currentSubRoute,
    expandedRoutes,
    handleToggleRoute,
    headerTitle,
    layoutPropsFromNav,
  } = useLayout({ userRoles, toggles });

  const siderWidth = sidebarOpen ? theme.layouts.layoutSider.width : theme.layouts.layoutSider.collapsedWidth;
  const headerHeight = theme.layouts.layoutHeader.height;

  const sidebarRoutes = useMemo(() => {
    const routeJsx = filteredRoutes.map(({ path, nav, children }) => {
      const { displayName = "", icon = "" } = nav || {};
      const IconComponent = getIcons()[icon] || getIcons()[displayName] || <IconUserOutlined size={5} />;
      const childrenToShow = children?.filter((child) => Boolean(child.nav));
      return (
        <Fragment key={path}>
          <Box data-tip={t(displayName)} data-for="routes-title">
            <Menu.Item
              icon={IconComponent}
              handleOnClick={() => {
                if (childrenToShow?.length) {
                  handleToggleRoute(path);
                } else {
                  navigate(path);
                }
              }}
              active={currentRoute?.path === path}
              iconOnly={!sidebarOpen}
            >
              {t(displayName)}
            </Menu.Item>
          </Box>
          {(expandedRoutes.includes(path) || currentRoute?.path === path) &&
            childrenToShow?.map(({ path: subPath, nav: subNav }) => {
              const { displayName = "", icon = "" } = subNav || {};
              const IconComponent = getIcons()[icon] || getIcons()[displayName] || <IconUserOutlined size={3} />;
              return (
                <Box data-tip={t(displayName)} key={subPath} data-for="routes-title">
                  <Menu.Item
                    isSubList
                    icon={IconComponent}
                    handleOnClick={() => {
                      navigate(path + subPath);
                    }}
                    active={currentSubRoute?.nav?.displayName.includes(displayName)}
                    iconOnly={!sidebarOpen}
                  >
                    {t(displayName)}
                  </Menu.Item>
                </Box>
              );
            })}
        </Fragment>
      );
    });
    routeJsx.push(<LogoutLink key={`logout ${routeJsx.length}`} sidebarOpen={sidebarOpen} />);
    return routeJsx;
  }, [
    filteredRoutes,
    currentRoute?.path,
    navigate,
    sidebarOpen,
    t,
    currentSubRoute?.nav?.displayName,
    expandedRoutes,
    handleToggleRoute,
  ]);

  const layoutSider = useMemo(
    () => (
      <LayoutSider
        sider={sider}
        sidebarRoutes={sidebarRoutes}
        sidebarOpen={sidebarOpen}
        userInitials={userInitials}
        unreadMessageCount={unreadMessageCount}
        updateSidebarState={updateSidebarState}
      />
    ),
    [sidebarOpen, sidebarRoutes, sider, unreadMessageCount, updateSidebarState, userInitials],
  );

  const toolTipComponent = useMemo(
    () => !sidebarOpen && <ReactTooltip id="routes-title" backgroundColor={getColor(theme, "secondaryDark")} />,
    [sidebarOpen, theme],
  );

  const layoutHeader = useMemo(
    () => (
      <>
        <LayoutHeader header={header} />
      </>
    ),
    [header],
  );

  return (
    <LayoutView
      siderWidth={siderWidth}
      headerTitle={headerTitle}
      headerHeight={headerHeight}
      tooltipComponent={toolTipComponent}
      footer={footer}
      header={layoutHeader}
      sider={layoutSider}
      {...layoutPropsFromNav}
    >
      {children}
    </LayoutView>
  );
};
